import {Injectable} from '@angular/core';
import {forkJoin, Observable, of, Subject, timer} from "rxjs";
import {UserViewModel} from "../../features/user/settings/user.view-model";
import {catchError, distinctUntilChanged, map, mergeMap, switchMap, tap} from "rxjs/operators";
import {QueryParamsApiModel} from "../../api/models/query-params-api.model";
import {FilterParameterApiModel} from "../../api/models/filter-parameter.api.model";
import {ResultListApiModel} from "../../api/models/result-list.api.model";
import {UserApiModel} from "../../api/models/user.api-model";
import {UserRestService} from "../../api/services/user.rest.service";
import {MediaObjectApiModel} from "../../api/models/media-object.api-model";
import {PersonalInformationViewModel} from "../../features/user/settings/personal-information/personal-information-view-model";
import {MediaObjectRestService} from "../../api/services/media-object.rest.service";
import {MediaObjectPipe} from "../../core/pipes/media-object.pipe";

@Injectable({
  providedIn: 'root'
})
export abstract class UsersQueryDataProviderService {

  public resultSubject$ = new Subject<string>();
  public resources: any[] = [];
  public loading = true;

  constructor(
    protected readonly userRS: UserRestService,
    protected readonly mediaObjectRS: MediaObjectRestService,
  ) {
  }

  public fetchResourcesFromQuerySubject(
    resolveInvitationCandidates?: (query: string) => Observable<UserApiModel[]>
  ): Observable<UserViewModel[][]> {
    return this.resultSubject$.pipe(
      distinctUntilChanged(),
      tap(() => {
        this.resources = [];
        this.loading = true;
      }),
      switchMap((query: string) => {
        return timer(1000).pipe(switchMap(() => {
          const publicUsers = this.fetchUsersByQuery(query);
          if (!resolveInvitationCandidates || query.length < 3) {
            return publicUsers;
          }
          return forkJoin([
            publicUsers,
            resolveInvitationCandidates(query).pipe(catchError(() => of([] as UserApiModel[]))),
          ]).pipe(map(([[visibleUsers], invitationCandidates]) => {
            const byId = new Map<string, UserViewModel>();
            for (const user of visibleUsers) {
              byId.set(user['@id'], user);
            }
            for (const candidate of invitationCandidates) {
              if (!byId.has(candidate['@id'])) {
                byId.set(candidate['@id'], new UserViewModel(candidate));
              }
            }
            const users = [...byId.values()];
            this.resources = users.map(user => this.createUserInterface(user))
              .sort(UsersQueryDataProviderService.sortByUsername);
            return [users];
          }));
        }));
      })).pipe(tap(() => this.loading = false));
  }

  public fetchUsersByQuery(query: string): Observable<(UserViewModel[])[]> {
    const queryParams = new QueryParamsApiModel();
    queryParams.filters.push(new FilterParameterApiModel('q', query, 'single'));

    return this.fetchUsers(queryParams);
  }


  public fetchUsers(queryParams: QueryParamsApiModel = new QueryParamsApiModel()): Observable<(UserViewModel[])[]> {
    return forkJoin([
      this.userRS.getCollection(queryParams)
    ]).pipe(mergeMap(([usersAM]: [ResultListApiModel<UserApiModel>]) => {
      return this.forkDeps([usersAM]);
    }));
  }

  private forkDeps([usersAM]: [ResultListApiModel<UserApiModel>]): Observable<(UserViewModel[])[]> {
    return forkJoin([
      this.getMediaObjectCollection(usersAM.records),
    ]).pipe(mergeMap(([mediaObjectAMs]: [MediaObjectApiModel[]]) => {
      const users = usersAM.records.map((userAM: UserApiModel) => {
        userAM.avatar = mediaObjectAMs.find(m => m['@id'] === userAM.avatar) || userAM.avatar;
        return new UserViewModel(userAM);
      });
      this.resources = users.map(user => this.createUserInterface(user))
        .sort(UsersQueryDataProviderService.sortByUsername);
      return of([users]);
    }));
  }

  private getMediaObjectCollection(resultAMs: UserApiModel[]): Observable<MediaObjectApiModel[]> {
    const params = new QueryParamsApiModel();
    params.filters = resultAMs
      .filter((e: UserApiModel) => typeof e.avatar === 'string')
      .map(e => new FilterParameterApiModel('id', e.avatar));
    if (params.filters.length) {
      return this.mediaObjectRS.getMediaObjectCollection(params)
        .pipe(map((resultListAM: ResultListApiModel<MediaObjectApiModel>) => {
          return resultListAM.records; //.map((mediaObjectAM: MediaObjectApiModel) => new MediaObjectViewModel(mediaObjectAM));
        }));
    }

    return of<MediaObjectApiModel[]>([]);
  }

  public static sortByUsername(a: { alias: string }, b: { alias: string }): number {
    return ('' + a.alias).localeCompare(b.alias);
  }

  public createUserInterface(user: UserViewModel): object {
    const info = user.info instanceof PersonalInformationViewModel ? user.info : null;
    return Object.assign({
      name: user.name || (info && info.firstName && info.lastName ? info.firstName + ' ' + info.lastName : null),
      alias: user.username,
      email: user.email,
      uri: '/' + user.slug,
      slug: user.slug,
      id: user.id,
      user: user.toApiModel(),
      avatar: MediaObjectPipe.transformToLink(user.avatar, {formatType: 'thumb_sm', defaultImageType: 'avatar'}),
      type: 'user'
    });
  }
}
